import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import SoftAlert from "../../../components/Alert";
import { resetPasswordWithToken } from "../../../api/auth/authApiClient";

function SetNewPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const initialResetToken = location.state?.resetToken || null;

  const [resetToken, setResetToken] = useState(initialResetToken);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we don't have an email or reset token, redirect back to reset-password flow
    if (!email || !resetToken) {
      navigate("/authentication/reset-password");
    }
  }, [email, resetToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resetToken) {
      setError("Password reset token is missing. Please restart the reset process.");
      setShowAlert(true);
      return;
    }

    if (!password || !confirmPassword) {
      setError("Please enter password and confirm password");
      setShowAlert(true);
      return;
    }

    if (password !== confirmPassword) {
      setError("Password and Confirm Password do not match");
      setShowAlert(true);
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      setShowAlert(true);
      return;
    }

    setLoading(true);
    try {
      setError(null);
      setSuccess(null);
      setShowAlert(false);

      await resetPasswordWithToken(resetToken, password);

      setSuccess("Password reset successful. You can now sign in with your new password.");
      setShowAlert(true);

      // Redirect to sign-in after a short delay
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 5000);
    } catch (err) {
      setError(err?.message || "Failed to reset password");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CoverLayout
      title={success ? "" : "Set New Password"}
      description={success ? "" : "Enter your new password below"}
    >
      {error && (
        <SoftAlert
          color="error"
          dismissible={false}
          timeout={10}
          mount={showAlert}
          onClose={() => {
            setError(null);
            setShowAlert(false);
          }}
        >
          {error}
        </SoftAlert>
      )}
      {success && !error && (
        <SoftAlert
          color="success"
          dismissible={false}
          timeout={10}
          mount={showAlert}
          onClose={() => {
            setSuccess(null);
            setShowAlert(false);
          }}
        >
          {success}
        </SoftAlert>
      )}
      {!success && (
        <SoftBox component="form" role="form" onSubmit={handleSubmit}>
          <SoftBox mb={2}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                New Password
              </SoftTypography>
            </SoftBox>
            <SoftInput
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </SoftBox>
          <SoftBox mb={2}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                Confirm New Password
              </SoftTypography>
            </SoftBox>
            <SoftInput
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </SoftBox>
          <SoftBox mt={4} mb={1}>
            <SoftButton
              type="submit"
              variant="gradient"
              color="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? "Saving..." : "Save New Password"}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      )}
    </CoverLayout>
  );
}

export default SetNewPassword;


